/**
 * Region → calendar engine.
 * The user's region (profile timezone) decides which calendar the whole app
 * renders: Iran renders the Persian (Jalali/Solar Hijri) calendar, every other
 * region renders Gregorian. Data is always stored as Gregorian ISO strings —
 * only display goes through this engine.
 */
export type CalendarSystem = "gregory" | "persian";

const REGION_STORAGE_KEY = "nelurio.region";

const PERSIAN_TIMEZONES = new Set(["Asia/Tehran"]);

export function regionToCalendar(timezone?: string | null): CalendarSystem {
  if (timezone && PERSIAN_TIMEZONES.has(timezone)) return "persian";
  return "gregory";
}

export function readRegion(): string {
  try {
    return window.localStorage.getItem(REGION_STORAGE_KEY) || inferRegion();
  } catch {
    return inferRegion();
  }
}

export function storeRegion(timezone: string) {
  try { window.localStorage.setItem(REGION_STORAGE_KEY, timezone); } catch { /* ignore */ }
}

/** Best-effort guess from the browser before the profile loads. */
function inferRegion(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin";
  } catch {
    return "Europe/Berlin";
  }
}

/**
 * Intl locale tag for a display format. Persian calendar always renders with
 * Persian month names/digits; Gregorian keeps the UI language but forces
 * Gregorian for fa/ar so labels and numbers agree.
 */
export function displayTag(locale: string, calendar: CalendarSystem): string {
  if (calendar === "persian") return "fa-IR-u-ca-persian-nu-arabext";
  if (locale === "fa-IR") return "fa-IR-u-ca-gregory";
  if (locale === "ar") return "ar-u-ca-gregory";
  return locale;
}

const persianPartsFormatter = new Intl.DateTimeFormat("en-u-ca-persian", { year: "numeric", month: "numeric", day: "numeric" });

/** Gregorian Date → Jalali (year, month, day) via Intl, no hand-rolled math. */
export function persianParts(date: Date): { year: number; month: number; day: number } {
  const parts = persianPartsFormatter.formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) => Number((parts.find((part) => part.type === type)?.value ?? "0").replace(/[^0-9]/g, ""));
  return { year: read("year"), month: read("month"), day: read("day") };
}

/** First Gregorian day of the Jalali month containing `date`. */
export function persianMonthStart(date: Date): Date {
  const { day } = persianParts(date);
  const start = new Date(date);
  start.setDate(start.getDate() - (day - 1));
  return start;
}

/** All Gregorian days inside the Jalali month containing `date`. */
export function persianMonthDays(date: Date): Date[] {
  const days: Date[] = [];
  const cursor = persianMonthStart(date);
  const month = persianParts(cursor).month;
  while (persianParts(cursor).month === month) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}
